import React, { ReactElement } from 'react';
import {
  AfterSubmitFunc,
  CheckboxInput,
  FormTemplate,
  IfFormData,
  RelativeRow,
  SubmitFormFunc,
  TextArea,
  TextInput,
} from 'ui-builder';
import Separator from '../../../../shared/web/uitheme/form/Separator';
import TransactionPartiesInput from './TransactionPartiesInput';
import useAuthorizationChecker from '../../../../shared/authorization/authorizationChecker';
import { useProjectTypeAndSetIdContextRequired } from '../../../view/ViewPlan';
import {
  DueDateConfigInput,
  PlanLinkedFieldDropdown,
  TasksGroupsDropdown,
  RoadmapDescription, PortalRoadmapCheckbox
} from 'features/tasks-forms';
import { ApiVersion, PlanTask, TeamPermission } from 'nekst-api';
import { ValidationSchema } from 'validation-schema';
import { PlanPermission } from 'authorization-scope';
import { EmptyCell } from '../../../../shared/web/uitheme/form/RelativeRow';
import { BusinessDaysToggleWrapper  } from 'feature-toggle';
import { IfWeekendDropdown } from 'features/nekst-widgets';
import DueDateRow from '../DueDateRow';

export interface BaseStandardTaskFormProps {
  title: string,
  submitFormFunc: SubmitFormFunc<PlanTask>,
  afterSubmitFunc?: AfterSubmitFunc<PlanTask>,
  validationSchema: ValidationSchema,
  buttons?: ReactElement,
  initialData?: PlanTask
  planId: number
}

function CheckboxesRow() {
  const { isGranted } = useAuthorizationChecker();

  if (isGranted(TeamPermission.FEATURE_BUSINESS_DAYS)) {
    return (
      <RelativeRow weights={[2.5, 2, 3, 4.5]}>
        <CheckboxInput source="isKeyDate" label="Key Date" />
        <CheckboxInput source="isPrivate" label="Private" />
        <CheckboxInput source="useBusinessDaysCalculation" label="Business Days" />
        <PortalRoadmapCheckbox />
      </RelativeRow>
    );
  } else {
    return (
      <RelativeRow weights={[3, 3, 3, 3]}>
        <CheckboxInput source="isKeyDate" label="Key Date" />
        <CheckboxInput source="isPrivate" label="Private" />
        <CheckboxInput source="isDuringBusinessWeek" label="Only During Business Week" />
        <PortalRoadmapCheckbox />
      </RelativeRow>
    );
  }
}

export default function BaseStandardTaskForm(props: BaseStandardTaskFormProps) {
  const { isGranted } = useAuthorizationChecker();

  const typeAndSetIdContext = useProjectTypeAndSetIdContextRequired();

  return (
    <FormTemplate<PlanTask & { copyRoadmapDescription?: boolean }>
      submitFormFunc={props.submitFormFunc}
      afterSubmitFunc={props.afterSubmitFunc}
      validationSchema={props.validationSchema}
      title={props.title}
      buttons={props.buttons}
      initialData={{
        copyRoadmapDescription: !props.initialData?.roadmapDescription,
        ...props.initialData,
        _version: isGranted(TeamPermission.FEATURE_BUSINESS_DAYS) ? ApiVersion.IF_WEEKEND_SHIFT : undefined
      }}
      isDisabled={!isGranted(PlanPermission.UPDATE)}
    >
      <TextInput source="name" label="Task Name" />
      <CheckboxesRow />
      <IfFormData conditionFunc={(data) => !data.parentTaskRelation}>
        <RelativeRow weights={[6, 1, 5]}>
          <DueDateConfigInput source="dueDateConfig" label="Due" />
          <EmptyCell />
          <BusinessDaysToggleWrapper>
            <IfWeekendDropdown />
          </BusinessDaysToggleWrapper>
        </RelativeRow>
      </IfFormData>
      <DueDateRow />
      <Separator />
      <RelativeRow weights={typeAndSetIdContext.projectType ? [4, 6, 2] : [6, 4, 2]}>
        {typeAndSetIdContext.projectType && (
          <PlanLinkedFieldDropdown
            {...typeAndSetIdContext}
            source="linkedFieldId"
            parentTaskIdSource="parentTaskRelation.parentTaskId"
            label="Link to Detail Box"
            planId={props.planId}
          />
        )}
        <TasksGroupsDropdown source="groupId" label="Group" planId={props.planId} />
      </RelativeRow>
      <Separator />
      <TextArea source="description" label="Task Description" />
      <RoadmapDescription />
      <Separator />
      <TransactionPartiesInput
        source="transactionParties"
        label="Transaction Parties"
        planId={props.planId}
      />
    </FormTemplate>
  );
}
